import Arweave from 'arweave';
import { secureStorage } from '../utils/security';

/**
 * ArweaveService - Handles decentralized, permanent image storage on Arweave
 * Provides censorship-resistant storage for profile pictures and banner images
 */
export class ArweaveService {
  constructor() {
    // Development mode flag
    this.isDevelopmentMode = process.env.NODE_ENV === 'development';
    
    // Initialize Arweave client for mainnet
    this.arweave = Arweave.init({
      host: 'arweave.net',
      port: 443,
      protocol: 'https',
      timeout: 20000,
      logging: false,
    });

    // For development, you can use testnet:
    // this.arweave = Arweave.init({
    //   host: '127.0.0.1',
    //   port: 1984,
    //   protocol: 'http',
    // });

    this.wallet = null;
    this.isInitialized = false;
    this.mockTransactionCounter = 1;
  }

  /**
   * Initialize the service with user's Arweave wallet
   * Supports both ArConnect wallet (production) and demo wallet (development)
   */
  async initialize() {
    try {
      // Check if we're in production and ArConnect is available
      if (process.env.NODE_ENV === 'production' && window.arweaveWallet) {
        console.log('🔗 Connecting to ArConnect wallet...');
        await this.connectArConnectWallet();
      } else {
        console.log('🔧 Using demo wallet for development...');
        await this.initializeDemoWallet();
      }
      
      this.isInitialized = true;
      console.log('✅ ArweaveService initialized successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Failed to initialize ArweaveService:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Connect to user's ArConnect wallet (production)
   */
  async connectArConnectWallet() {
    try {
      // Request connection to ArConnect
      await window.arweaveWallet.connect(['ACCESS_ADDRESS', 'SIGN_TRANSACTION']);
      
      // Get wallet address
      this.walletAddress = await window.arweaveWallet.getActiveAddress();
      
      // Set up wallet for transactions
      this.wallet = 'arconnect'; // Special flag for ArConnect
      
      console.log('🔗 ArConnect wallet connected:', this.walletAddress);
      return { success: true, address: this.walletAddress };
    } catch (error) {
      console.error('❌ ArConnect connection failed:', error);
      throw new Error('Failed to connect ArConnect wallet: ' + error.message);
    }
  }

  /**
   * Initialize demo wallet for development
   */
  async initializeDemoWallet() {
    try {
      this.wallet = await this.arweave.wallets.generate();
      this.walletAddress = await this.arweave.wallets.jwkToAddress(this.wallet);
      
      console.log('🔧 Demo wallet generated:', this.walletAddress);
      return { success: true, address: this.walletAddress };
    } catch (error) {
      console.error('❌ Demo wallet generation failed:', error);
      throw new Error('Failed to generate demo wallet: ' + error.message);
    }
  }

  /**
   * Upload media content (images, videos, GIFs) to Arweave
   * @param {File} file - The media file to upload
   * @param {Object} metadata - Additional metadata for the media
   * @returns {Promise<Object>} - Upload result with transaction ID
   */
  async uploadMedia(file, metadata = {}) {
    return this.uploadImage(file, metadata);
  }

  /**
   * Upload an image file to Arweave
   * @param {File} file - The image file to upload
   * @param {Object} metadata - Additional metadata for the image
   * @returns {Promise<Object>} - Upload result with transaction ID
   */
  async uploadImage(file, metadata = {}) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    // Development mode - simulate upload without requiring AR tokens
    if (this.isDevelopmentMode) {
      return this.simulateUpload(file, metadata, 'image');
    }

    try {
      console.log('📤 Uploading image to Arweave...', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      // Create transaction
      const transaction = await this.arweave.createTransaction({ data: file });
      
      // Add metadata tags
      transaction.addTag('Content-Type', file.type);
      transaction.addTag('App-Name', 'Znoospace');
      transaction.addTag('Type', 'image');
      transaction.addTag('Upload-Timestamp', Date.now().toString());
      
      // Add custom metadata
      Object.entries(metadata).forEach(([key, value]) => {
        transaction.addTag(key, value.toString());
      });
      
      // Sign transaction based on wallet type
      if (this.wallet === 'arconnect') {
        // Use ArConnect for signing
        await window.arweaveWallet.sign(transaction);
      } else {
        // Use demo wallet for signing
        await this.arweave.transactions.sign(transaction, this.wallet);
      }

      // Sign the transaction
      await this.arweave.transactions.sign(transaction, this.wallet);

      // Get uploader for progress tracking
      const uploader = await this.arweave.transactions.getUploader(transaction);

      // Upload with progress tracking
      while (!uploader.isComplete) {
        await uploader.uploadChunk();
        const progress = Math.round(uploader.pctComplete);
        console.log(`📤 Upload progress: ${progress}% (${uploader.uploadedChunks}/${uploader.totalChunks} chunks)`);
      }

      const transactionId = transaction.id;
      const arweaveUrl = `https://arweave.net/${transactionId}`;

      console.log('✅ Image uploaded to Arweave successfully!', {
        transactionId,
        arweaveUrl,
        fileSize: file.size,
        fileName: file.name
      });

      // Cache the upload result
      await this.cacheUploadResult(transactionId, {
        url: arweaveUrl,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        uploadedAt: new Date().toISOString(),
        metadata
      });

      return {
        success: true,
        transactionId,
        url: arweaveUrl,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      };

    } catch (error) {
      console.error('❌ Failed to upload image to Arweave:', error);
      return {
        success: false,
        error: error.message || 'Upload failed'
      };
    }
  }

  /**
   * Upload profile picture to Arweave
   * @param {File} file - Profile picture file
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<Object>} - Upload result
   */
  async uploadProfilePicture(file, walletAddress) {
    return await this.uploadImage(file, {
      category: 'profile',
      walletAddress,
      timestamp: Date.now()
    });
  }

  /**
   * Upload banner image to Arweave
   * @param {File} file - Banner image file
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<Object>} - Upload result
   */
  async uploadBannerImage(file, walletAddress) {
    return await this.uploadImage(file, {
      category: 'banner',
      walletAddress,
      timestamp: Date.now()
    });
  }

  /**
   * Get image data from Arweave
   * @param {string} transactionId - Arweave transaction ID
   * @returns {Promise<Object>} - Image data and metadata
   */
  async getImage(transactionId) {
    try {
      // Check cache first
      const cached = await this.getCachedUploadResult(transactionId);
      if (cached) {
        return {
          success: true,
          url: `https://arweave.net/${transactionId}`,
          ...cached
        };
      }

      // Fetch from Arweave
      const transaction = await this.arweave.transactions.get(transactionId);
      const data = await this.arweave.transactions.getData(transactionId, { decode: true });

      return {
        success: true,
        transactionId,
        url: `https://arweave.net/${transactionId}`,
        data,
        tags: transaction.tags
      };

    } catch (error) {
      console.error('❌ Failed to get image from Arweave:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Check if a transaction is confirmed on Arweave
   * @param {string} transactionId - Transaction ID to check
   * @returns {Promise<Object>} - Confirmation status
   */
  async checkTransactionStatus(transactionId) {
    try {
      const status = await this.arweave.transactions.getStatus(transactionId);
      return {
        success: true,
        confirmed: status.confirmed,
        blockHeight: status.block_height,
        blockIndep_hash: status.block_indep_hash
      };
    } catch (error) {
      console.error('❌ Failed to check transaction status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get user's Arweave balance (for demo purposes)
   * @returns {Promise<Object>} - Balance information
   */
  async getBalance() {
    if (!this.wallet) {
      return { success: false, error: 'Wallet not initialized' };
    }

    try {
      const address = await this.arweave.wallets.jwkToAddress(this.wallet);
      const balance = await this.arweave.wallets.getBalance(address);
      const arBalance = this.arweave.ar.winstonToAr(balance);

      return {
        success: true,
        address,
        balance: arBalance,
        balanceWinston: balance
      };
    } catch (error) {
      console.error('❌ Failed to get Arweave balance:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Convert File to ArrayBuffer
   * @param {File} file - File to convert
   * @returns {Promise<ArrayBuffer>} - ArrayBuffer representation
   */
  async fileToArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Cache upload result for faster retrieval
   * @param {string} transactionId - Transaction ID
   * @param {Object} data - Upload result data
   */
  async cacheUploadResult(transactionId, data) {
    try {
      await secureStorage.setItem(`arweave_upload_${transactionId}`, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to cache upload result:', error);
    }
  }

  /**
   * Get cached upload result
   * @param {string} transactionId - Transaction ID
   * @returns {Promise<Object|null>} - Cached data or null
   */
  async getCachedUploadResult(transactionId) {
    try {
      const cached = await secureStorage.getItem(`arweave_upload_${transactionId}`);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.warn('Failed to get cached upload result:', error);
      return null;
    }
  }

  /**
   * Validate image file before upload
   * @param {File} file - File to validate
   * @returns {Object} - Validation result
   */
  /**
   * Upload a post with optional media to Arweave
   * @param {string} content - Post content
   * @param {File|null} mediaFile - Optional media file
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} - Upload result
   */
  async uploadPost(content, mediaFile = null, metadata = {}) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    // Development mode - simulate upload without requiring AR tokens
    if (this.isDevelopmentMode) {
      return this.simulatePostUpload(content, mediaFile, metadata);
    }

    try {
      console.log('📤 Uploading post to Arweave...', {
        hasMedia: !!mediaFile,
        contentLength: content.length
      });

      let mediaUrl = null;
      let mediaTransactionId = null;

      // Upload media first if provided
      if (mediaFile) {
        const mediaValidation = this.validateMediaFile(mediaFile);
        if (!mediaValidation.valid) {
          return {
            success: false,
            error: `Media validation failed: ${mediaValidation.error}`
          };
        }

        const mediaResult = await this.uploadMedia(mediaFile, {
          ...metadata,
          category: 'post-media',
          postContent: content.substring(0, 100) // First 100 chars for reference
        });

        if (!mediaResult.success) {
          return {
            success: false,
            error: `Media upload failed: ${mediaResult.error}`
          };
        }

        mediaUrl = mediaResult.url;
        mediaTransactionId = mediaResult.transactionId;
      }

      // Create post data object
      const postData = {
        content,
        mediaUrl,
        mediaTransactionId,
        timestamp: new Date().toISOString(),
        app: 'Znoospace',
        version: '1.0.0',
        ...metadata
      };

      // Convert post data to ArrayBuffer
      const postDataString = JSON.stringify(postData, null, 2);
      const arrayBuffer = new TextEncoder().encode(postDataString);

      // Create transaction with post data
      const transaction = await this.arweave.createTransaction({
        data: arrayBuffer
      }, this.wallet);

      // Add tags for better discoverability
      transaction.addTag('Content-Type', 'application/json');
      transaction.addTag('App-Name', 'Znoospace');
      transaction.addTag('App-Version', '1.0.0');
      transaction.addTag('Type', 'social-post');
      transaction.addTag('Has-Media', mediaFile ? 'true' : 'false');
      
      if (metadata.walletAddress) {
        transaction.addTag('Author', metadata.walletAddress);
      }
      if (metadata.username) {
        transaction.addTag('Username', metadata.username);
      }
      if (mediaFile) {
        transaction.addTag('Media-Type', mediaFile.type);
        transaction.addTag('Media-Transaction', mediaTransactionId);
      }

      // Sign and upload
      await this.arweave.transactions.sign(transaction, this.wallet);
      const uploader = await this.arweave.transactions.getUploader(transaction);

      while (!uploader.isComplete) {
        await uploader.uploadChunk();
        const progress = Math.round(uploader.pctComplete);
        console.log(`📤 Post upload progress: ${progress}%`);
      }

      const transactionId = transaction.id;
      const arweaveUrl = `https://arweave.net/${transactionId}`;

      console.log('✅ Post uploaded to Arweave successfully!', {
        transactionId,
        arweaveUrl,
        hasMedia: !!mediaFile,
        mediaTransactionId
      });

      return {
        success: true,
        transactionId,
        url: arweaveUrl,
        mediaUrl,
        mediaTransactionId,
        postData
      };

    } catch (error) {
      console.error('❌ Failed to upload post to Arweave:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get post data from Arweave
   * @param {string} transactionId - Arweave transaction ID
   * @returns {Promise<Object>} - Post data
   */
  async getPost(transactionId) {
    try {
      const response = await fetch(`https://arweave.net/${transactionId}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const postData = await response.json();
      return {
        success: true,
        data: postData
      };
    } catch (error) {
      console.error('❌ Failed to get post from Arweave:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  validateImageFile(file) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Invalid file type. Please use JPEG, PNG, GIF, or WebP.' };
    }

    if (file.size > maxSize) {
      return { valid: false, error: 'File too large. Maximum size is 10MB.' };
    }

    return { valid: true };
  }

  /**
   * Validate media file (images, videos, GIFs) before upload
   * @param {File} file - File to validate
   * @returns {Object} - Validation result
   */
  validateMediaFile(file) {
    const maxSize = 50 * 1024 * 1024; // 50MB for videos
    const allowedTypes = [
      // Images
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      // Videos
      'video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov'
    ];

    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { 
        valid: false, 
        error: 'Invalid file type. Please use JPEG, PNG, GIF, WebP for images or MP4, WebM, OGG for videos.' 
      };
    }

    // Different size limits for different types
    const isVideo = file.type.startsWith('video/');
    const sizeLimit = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024; // 50MB for video, 10MB for images

    if (file.size > sizeLimit) {
      const limitMB = isVideo ? '50MB' : '10MB';
      return { 
        valid: false, 
        error: `File too large. Maximum size is ${limitMB} for ${isVideo ? 'videos' : 'images'}.` 
      };
    }

    return { valid: true };
  }

  /**
   * Simulate upload for development mode
   * @param {File} file - File to simulate uploading
   * @param {Object} metadata - Metadata
   * @param {string} type - Upload type
   * @returns {Promise<Object>} - Simulated upload result
   */
  async simulateUpload(file, metadata = {}, type = 'media') {
    console.log('💻 Development Mode: Simulating Arweave upload...', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      type
    });

    // Simulate upload delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    const mockTransactionId = `dev_arweave_${type}_${this.mockTransactionCounter++}_${Date.now()}`;
    const mockUrl = `https://arweave.net/${mockTransactionId}`;

    console.log('✅ Simulated upload completed!', {
      transactionId: mockTransactionId,
      url: mockUrl,
      note: 'This is a simulated upload for development'
    });

    return {
      success: true,
      transactionId: mockTransactionId,
      url: mockUrl,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      simulated: true,
      uploadedAt: new Date().toISOString()
    };
  }

  /**
   * Simulate post upload for development mode
   * @param {string} content - Post content
   * @param {File|null} mediaFile - Optional media file
   * @param {Object} metadata - Metadata
   * @returns {Promise<Object>} - Simulated upload result
   */
  async simulatePostUpload(content, mediaFile = null, metadata = {}) {
    console.log('💻 Development Mode: Simulating Arweave post upload...', {
      hasMedia: !!mediaFile,
      contentLength: content.length
    });

    let mediaUrl = null;
    let mediaTransactionId = null;

    // Simulate media upload if provided
    if (mediaFile) {
      const mediaValidation = this.validateMediaFile(mediaFile);
      if (!mediaValidation.valid) {
        return {
          success: false,
          error: `Media validation failed: ${mediaValidation.error}`
        };
      }

      const mediaResult = await this.simulateUpload(mediaFile, metadata, 'post-media');
      if (!mediaResult.success) {
        return {
          success: false,
          error: `Media upload failed: ${mediaResult.error}`
        };
      }

      mediaUrl = mediaResult.url;
      mediaTransactionId = mediaResult.transactionId;
    }

    // Simulate upload delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    const mockTransactionId = `dev_arweave_post_${this.mockTransactionCounter++}_${Date.now()}`;
    const mockUrl = `https://arweave.net/${mockTransactionId}`;

    const postData = {
      content,
      mediaUrl,
      mediaTransactionId,
      timestamp: new Date().toISOString(),
      app: 'Znoospace',
      version: '1.0.0',
      simulated: true,
      ...metadata
    };

    console.log('✅ Simulated post upload completed!', {
      transactionId: mockTransactionId,
      url: mockUrl,
      hasMedia: !!mediaFile,
      note: 'This is a simulated upload for development'
    });

    return {
      success: true,
      transactionId: mockTransactionId,
      url: mockUrl,
      mediaUrl,
      mediaTransactionId,
      postData,
      simulated: true
    };
  }

  /**
   * Generate a demo wallet for testing (DO NOT use in production)
   * @returns {Promise<Object>} - Demo wallet
   */
  async generateDemoWallet() {
    try {
      const wallet = await this.arweave.wallets.generate();
      const address = await this.arweave.wallets.jwkToAddress(wallet);
      
      console.log('🔑 Generated demo Arweave wallet:', {
        address,
        note: 'This is a demo wallet for testing purposes only'
      });

      return {
        success: true,
        wallet,
        address
      };
    } catch (error) {
      console.error('❌ Failed to generate demo wallet:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Export singleton instance
export const arweaveService = new ArweaveService();
